import { type InfiniteData, infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { flattenPages, nextLekParam } from '@/hooks/queries/pagination';
import { useAutoFetchNextPage } from '@/hooks/use-auto-fetch-next-page';
import {
  type AgreementsLek,
  dedupeLatestByPid,
  getTermsAgreementsPage,
  getTermsProPage,
  getTermsVersions,
  mapProTerms,
  mapTermsAgreements,
  type ProTermsLek,
  searchTermsAgreement,
  searchTermsPro,
  type TermsRow,
} from '@/services/api/terms';

// Stable module-level selects via flattenPages (pagination.ts) — the
// select-memoization rationale lives there (costly here: these remap the entire
// accumulated dataset on every select run).
type ProTermsPage = Awaited<ReturnType<typeof getTermsProPage>>;
type AgreementsPage = Awaited<ReturnType<typeof getTermsAgreementsPage>>;

// The inner `?? []` guards malformed/empty payloads BEFORE the mappers run — an
// undefined page.data would crash the mappers' .map() and drop the page into the
// root error boundary (flattenPages' own guard only covers getItems' result).
const selectAllProTerms = flattenPages((page: ProTermsPage) => mapProTerms(page.data ?? []));
const flattenAgreementRows = flattenPages((page: AgreementsPage) => mapTermsAgreements(page.data ?? []));
// Custom composition: dedupeLatestByPid runs on the WHOLE flattened
// list — it cannot live inside the per-page getItems.
const selectAllAgreements = (data: InfiniteData<AgreementsPage>): TermsRow[] => dedupeLatestByPid(flattenAgreementRows(data));
// 404 → null → empty list: "user has no agreement" is a successful result
const selectAgreementSearch = (data: Awaited<ReturnType<typeof searchTermsAgreement>>): TermsRow[] => (data ? mapTermsAgreements([data]) : []);
const selectVersions = (data: Awaited<ReturnType<typeof getTermsVersions>>) => data.versions;

export const TERMS_QUERY_KEY = ['compliance', 'terms'] as const;
export const TERMS_PRO_QUERY_KEY = [...TERMS_QUERY_KEY, 'pro'] as const;
export const TERMS_AGREEMENTS_QUERY_KEY = [...TERMS_QUERY_KEY, 'agreements'] as const;
export const TERMS_VERSIONS_QUERY_KEY = [...TERMS_QUERY_KEY, 'versions'] as const;

export const termsProInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: [...TERMS_PRO_QUERY_KEY, 'list'],
    queryFn: ({ pageParam }) => getTermsProPage(pageParam),
    initialPageParam: undefined as ProTermsLek | undefined,
    // Continue while a lek exists even when a scan page carried 0 rows (legacy
    // comment: "Continue loading if lek exists, regardless of whether current
    // page has terms").
    getNextPageParam: nextLekParam,
    select: selectAllProTerms,
  });

export const termsAgreementsInfiniteQueryOptions = (version?: string) =>
  infiniteQueryOptions({
    queryKey: [...TERMS_AGREEMENTS_QUERY_KEY, 'list', version ?? 'all'],
    queryFn: ({ pageParam }) => getTermsAgreementsPage({ lek: pageParam, version }),
    initialPageParam: undefined as AgreementsLek | undefined,
    // Deliberate fix: legacy stopped on any empty page even when a
    // lek remained, silently truncating the dataset — the lek-only condition is
    // used here, exactly like the Pro tab.
    getNextPageParam: nextLekParam,
    select: selectAllAgreements,
  });

/** Full-dataset background load of the legacy Pro terms (one LEK page at a time) */
export const useTermsProAll = (enabled: boolean) => {
  const query = useInfiniteQuery({ ...termsProInfiniteQueryOptions(), enabled });
  // Port of the legacy page-level while-loop: after each page settles,
  // fetch the next until the LEK chain is exhausted — always while enabled. Keeps
  // streaming while the tab is hidden because the hooks live at page level;
  // stop-on-error/Retry-resume semantics live in useAutoFetchNextPage.
  useAutoFetchNextPage(query, enabled);
  return query;
};

/** Full-dataset background load of the current terms agreements, optionally filtered to one version */
export const useTermsAgreementsAll = (enabled: boolean, version?: string) => {
  const query = useInfiniteQuery({ ...termsAgreementsInfiniteQueryOptions(version), enabled });
  // Same legacy while-loop port as useTermsProAll above.
  useAutoFetchNextPage(query, enabled);
  return query;
};

export const termsProSearchQueryOptions = (userSearchTerm: string) =>
  queryOptions({
    queryKey: [...TERMS_PRO_QUERY_KEY, 'search', userSearchTerm],
    queryFn: () => searchTermsPro(userSearchTerm),
    select: mapProTerms,
  });

export const useTermsProSearch = (userSearchTerm?: string) => useQuery({ ...termsProSearchQueryOptions(userSearchTerm ?? ''), enabled: Boolean(userSearchTerm) });

export const termsAgreementSearchQueryOptions = (identifier: string, version?: string) =>
  queryOptions({
    queryKey: [...TERMS_AGREEMENTS_QUERY_KEY, 'search', identifier, version ?? 'all'],
    queryFn: () => searchTermsAgreement({ identifier, version }),
    select: selectAgreementSearch,
  });

export const useTermsAgreementSearch = (identifier?: string, version?: string) =>
  useQuery({ ...termsAgreementSearchQueryOptions(identifier ?? '', version), enabled: Boolean(identifier) });

export const termsVersionsQueryOptions = () =>
  queryOptions({
    queryKey: TERMS_VERSIONS_QUERY_KEY,
    queryFn: getTermsVersions,
    select: selectVersions,
    // Legacy useTermsVersions is load-once per session; effective dates change rarely
    staleTime: Number.POSITIVE_INFINITY,
  });

export const useTermsVersions = () => useQuery(termsVersionsQueryOptions());
